export class Component {
  constructor(element, options = {}) {
    if (!element || !(element instanceof Element)) {
      throw new Error('Component requires a valid DOM element');
    }

    this.element = element;
    this.options = { ...this.defaultOptions, ...options };
    this.children = new Set();
    this.state = {};
    this.eventListeners = new Map();
    this.isInitialized = false;
    this.isDestroyed = false;

    const initResult = this.init();
    
    if (initResult && typeof initResult.then === 'function') {
      initResult.then(() => this.isInitialized = true);
    } else {
      this.isInitialized = true;
    }
  }

  get defaultOptions() {
    return {};
  }

  /**
   * Initialize the component. Can return a Promise for async initialization.
   * @returns {void | Promise<void>}
   */
  init() {
    this.isInitialized = true;
  }

  render() {}

  setState(newState) {
    const oldState = { ...this.state };
    this.state = { ...this.state, ...newState };
    
    if (JSON.stringify(oldState) !== JSON.stringify(this.state)) {
      this.render();
      this._triggerEvent('stateChanged', { oldState, newState: this.state });
    }
  }

  addListener(element, event, handler) {
    const boundHandler = handler.bind(this);
    element.addEventListener(event, boundHandler);

    if (!this.eventListeners.has(element)) {
      this.eventListeners.set(element, new Map());
    }
    if (!this.eventListeners.get(element).has(event)) {
      this.eventListeners.get(element).set(event, new Set());
    }
    
    this.eventListeners.get(element).get(event).add({ original: handler, bound: boundHandler });
  }

  removeListener(element, event, handler) {
    const events = this.eventListeners.get(element);
    if (!events?.has(event)) return;

    const listeners = events.get(event);
    for (const listener of listeners) {
      if (listener.original === handler) {
        element.removeEventListener(event, listener.bound);
        listeners.delete(listener);
        break;
      }
    }

    if (listeners.size === 0) {
      events.delete(event);
      if (events.size === 0) {
        this.eventListeners.delete(element);
      }
    }
  }

  addChild(component) {
    this.children.add(component);
    return component;
  }

  removeChild(component) {
    if (this.children.has(component)) {
      component.destroy();
      this.children.delete(component);
    }
  }

  destroy() {
    if (this.isDestroyed) return;

    this._triggerEvent('beforeDestroy');

    this.eventListeners.forEach((events, element) => {
      events.forEach((listeners, event) => {
        listeners.forEach(({ bound }) => element.removeEventListener(event, bound));
      });
    });

    this.eventListeners.clear();
    this.children.forEach(child => child.destroy());
    this.children.clear();
    this.isDestroyed = true;
    this._triggerEvent('destroy');
  }

  _triggerEvent(name, detail = {}) {
    if (!this.element || !(this.element instanceof Element)) return;

    this.element.dispatchEvent(new CustomEvent(`component:${name}`, {
      bubbles: true,
      detail: { component: this, ...detail }
    }));
  }

  $(selector) {
    return this.element?.querySelector(selector) || null;
  }

  $$(selector) {
    return this.element ? Array.from(this.element.querySelectorAll(selector)) : [];
  }
}

export function createComponent(ComponentClass, element, options = {}) {
  if (typeof element === 'string') {
    element = document.querySelector(element);
  }
  if (!element) {
    element = document.createElement('div');
  }
  return new ComponentClass(element, options);
}
